import { formatCents, formatCount, isOpenStatus, tt } from '@agos/shared'

/**
 * `/order-tabs` — §9.5's *na sekeru* screen, which has no legacy counterpart.
 *
 * §10A.1 gave the backend tabs: a channel with `allowsDeferredPayment` may leave its orders unpaid, they
 * accumulate on a tab, and the tab is settled later — in full or in parts, with the method chosen at
 * settlement. Nothing in the frontend has ever opened one.
 *
 * **Two things from `OrderTabService`'s own docblock decide how this screen reads.**
 *
 * > **Nothing is fiscalised until settlement, and that is not a simplification.** The FiskalPRO protocol
 * > (Integračný protokol 1.2, §3) has no unpaid document: `FPAY` carries the actual means of payment and a
 * > receipt is only a receipt when `FTCLOSE` returns 0x06 or 0x11. There is no way to register a sale that
 * > has not been paid for … That is also the treatment the tax authority expects: the taxable event is the
 * > payment.
 *
 * > An open tab is therefore **excluded from what a waiter is expected to hand over** at the end of a
 * > shift, and appears instead as an outstanding balance.
 *
 * So a tab is not missing money and the screen must not read like it is. It is money that has not become
 * a sale yet.
 *
 * **FB-182.** The feature is switched off and has never been used. `sales_channels` has exactly one channel
 * with `allowsDeferredPayment` — `na_sekeru` — and it is **inactive**; `order_tabs` holds **0** rows and
 * `tab_settlements` **0**. A screen that simply showed an empty list would read as *nobody owes anything*,
 * which is true and useless. It says the channel is off and where to switch it on, because that is the only
 * thing standing between this screen and its first tab.
 */

export interface TabOrder {
  id: number
  orderNumber: string
  type: string
  status: string
  totalCents: number
  createdAt: string | null
}

export interface TabSettlement {
  id: number
  amountCents: number
  paymentMethod: string
  employeeId: number | null
  settledAt: string | null
  note: string | null
}

export interface Tab {
  id: number
  name: string
  customerId: number | null
  status: string
  openedAt: string | null
  settledAt: string | null
  note: string | null
  orderedCents: number
  settledCents: number
  outstandingCents: number
  orders: TabOrder[]
  settlements: TabSettlement[]
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function cents(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value * 100)
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
  }
  return 0
}

function id(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function tabsOf(payload: unknown): Tab[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const tab = tabOf(entry)
    return tab === null ? [] : [tab]
  })
}

export function tabOf(payload: unknown): Tab | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  if (typeof row.id !== 'number') return null
  const orders = Array.isArray(row.orders) ? row.orders : []
  const settlements = Array.isArray(row.settlements) ? row.settlements : []
  return {
    id: row.id,
    // A tab is opened for a customer *or* for a name somebody will recognise later; one of the two is
    // always there, because the server refuses a tab with neither.
    name: text(row.name) ?? tt('bez mena'),
    customerId: id(row.customerId),
    status: text(row.status) ?? '',
    openedAt: text(row.openedAt),
    settledAt: text(row.settledAt),
    note: text(row.note),
    orderedCents: cents(row.orderedTotal),
    settledCents: cents(row.settledTotal),
    outstandingCents: cents(row.outstanding),
    orders: orders.flatMap((entry) => {
      const order = entry as Record<string, unknown>
      if (typeof order.id !== 'number') return []
      return [
        {
          id: order.id,
          orderNumber: text(order.orderNumber) ?? String(order.id),
          type: text(order.type) ?? '',
          status: text(order.status) ?? '',
          totalCents: cents(order.total),
          createdAt: text(order.createdAt),
        },
      ]
    }),
    settlements: settlements.flatMap((entry) => {
      const settlement = entry as Record<string, unknown>
      if (typeof settlement.id !== 'number') return []
      return [
        {
          id: settlement.id,
          amountCents: cents(settlement.amount),
          paymentMethod: text(settlement.paymentMethod) ?? '',
          employeeId: id(settlement.employeeId),
          settledAt: text(settlement.settledAt),
          note: text(settlement.note),
        },
      ]
    }),
  }
}

/** §9.148 — the predicate lives in `@agos/shared`, because the payment dialog asks it too. */
export function isOpen(tab: Tab): boolean {
  return isOpenStatus(tab.status)
}

/**
 * Oldest first, and the server already does this.
 *
 * `listing` orders by `opened_at` — *"because that is the order they need chasing in: a tab open since
 * Tuesday is the one to ask about"*. Re-sorting here would undo a decision the backend made on purpose,
 * so this only asserts it.
 */
export function oldestFirst(tabs: readonly Tab[]): Tab[] {
  return [...tabs].sort((left, right) => (left.openedAt ?? '').localeCompare(right.openedAt ?? ''))
}

/** How long a tab has been running. The number a manager chases on. */
export function daysOpen(tab: Tab, now: number): number | null {
  if (tab.openedAt === null) return null
  const opened = Date.parse(tab.openedAt)
  if (Number.isNaN(opened) || opened > now) return null
  return Math.floor((now - opened) / 86_400_000)
}

export function ageLabel(days: number | null): string {
  if (days === null) return '-'
  if (days === 0) return tt('dnes')
  if (days === 1) return tt('včera')
  return `${formatCount(days)} ${tt('dní')}`
}

/** A tab nobody has touched for a fortnight is the one worth a phone call. */
export function isStale(days: number | null): boolean {
  return days !== null && days >= 14
}

// ─── The totals ──────────────────────────────────────────────────────────────

export interface TabTotals {
  tabs: number
  orderedCents: number
  settledCents: number
  outstandingCents: number
}

export function totalsOf(tabs: readonly Tab[]): TabTotals {
  // Plain integer reduces: `sumCents` parses its inputs and would multiply cents by a hundred.
  return {
    tabs: tabs.length,
    orderedCents: tabs.reduce((total, tab) => total + tab.orderedCents, 0),
    settledCents: tabs.reduce((total, tab) => total + tab.settledCents, 0),
    outstandingCents: tabs.reduce((total, tab) => total + tab.outstandingCents, 0),
  }
}

/**
 * What the outstanding total is, said so it cannot be read as missing cash.
 *
 * This is the sentence §9.5 asks to be visible *"where cash expectations show"*, and it is the one thing
 * about tabs that is easy to get wrong: an open tab is deliberately **not** part of what a waiter hands
 * over, because the sale has not been registered yet.
 */
export function outstandingNote(totals: TabTotals): string {
  if (totals.tabs === 0) return ''
  return `${formatCents(totals.outstandingCents)} ${tt('je otvorené na')} ${formatCount(totals.tabs)} ${tt('účtoch. Nie je to chýbajúca hotovosť - objednávka na účte sa zaregistruje až pri úhrade, takže do očakávanej tržby zmeny sa nepočíta.')}`
}

/** FB-182: the tab feature is off, and that is why the list is empty. */
export function featureNote(deferredChannels: readonly { name: string; isActive: boolean }[]): string {
  if (deferredChannels.length === 0) {
    return tt(
      'Žiadny predajný kanál nemá povolenú platbu neskôr, takže účet sa nedá otvoriť. Zapnúť sa to dá v Predajných kanáloch.',
    )
  }
  if (deferredChannels.every((channel) => !channel.isActive)) {
    const names = deferredChannels.map((channel) => channel.name).join(', ')
    return `${tt('Kanál s platbou neskôr')} (${names}) ${tt('je vypnutý, takže na účet sa zatiaľ nedá nič dať. Zapnúť sa dá v Predajných kanáloch.')}`
  }
  return ''
}

// ─── Settling ────────────────────────────────────────────────────────────────

/** The methods the till knows. A settlement is a real payment, so the list is the till's own. */
export const PAYMENT_METHODS: readonly string[] = ['cash', 'card_terminal', 'card_courier', 'card_online']

export function paymentLabel(method: string): string {
  if (method === 'cash') return tt('Hotovosť')
  if (method === 'card_terminal') return tt('Karta - terminál')
  if (method === 'card_courier') return tt('Karta u kuriéra')
  if (method === 'card_online') return tt('Online platba')
  return method === '' ? tt('neuvedené') : method
}

export interface SettleForm {
  amount: string
  paymentMethod: string
  note: string
}

export function settleFormFor(tab: Tab): SettleForm {
  // Defaults to the whole balance, which is what the server does when `amount` is omitted.
  //
  // A **form value**, not a rendered figure: this goes into a text input the operator edits and the API
  // parses, so it carries a decimal point. N-02's rule is about what reaches the screen as a number — and
  // the form accepts a comma back, because a Slovak keyboard types one.
  // eslint-disable-next-line no-restricted-syntax -- form value, parsed by the input and by the API
  const amount = (tab.outstandingCents / 100).toFixed(2)
  return { amount, paymentMethod: 'cash', note: '' }
}

// ─── Opening ─────────────────────────────────────────────────────────────────

export interface OpenForm {
  name: string
  note: string
}

export function openFormFor(): OpenForm {
  return { name: '', note: '' }
}

export type OpenProblem = 'no-name'

/**
 * Why this tab cannot be opened.
 *
 * <p><b>The server's rule, said here so the button can say it first.</b>
 * `OrderTabService.create` reads the name through a helper that turns blank into `null` and then
 * refuses: *"Účet potrebuje meno alebo zákazníka"*. So an empty name is not a tab without a label —
 * it is a 400.
 *
 * <p>Until 2026-08-11 the open dialog was a `ConfirmDialog`: a title, a description and two buttons,
 * with **no field to type a name into**. It always sent `name: ''`, so **every** attempt to open a
 * tab failed, and with no tab there was nothing to add an order to or take a payment on — which is
 * how the whole screen read as dead. The owner: *"I can only press Otvoriť or Zrušiť, I cant write
 * anything"*.
 */
export function openTabProblems(form: OpenForm): OpenProblem[] {
  return form.name.trim() === '' ? ['no-name'] : []
}

export function openProblemMessage(problem: OpenProblem): string {
  return problem === 'no-name'
    ? tt('Účet potrebuje meno, ktoré niekto neskôr spozná.')
    : String(problem)
}

export function canOpen(form: OpenForm): boolean {
  return openTabProblems(form).length === 0
}

/** The body `POST /order-tabs` is sent. A blank note is omitted rather than sent empty. */
export function openPayload(form: OpenForm): Record<string, unknown> {
  const payload: Record<string, unknown> = { name: form.name.trim() }
  const note = form.note.trim()
  if (note !== '') payload.note = note
  return payload
}

export type SettleProblem = 'not-a-number' | 'not-positive' | 'over-outstanding' | 'no-method'

/**
 * What the server would refuse, refused first.
 *
 * `settle` rejects a non-positive amount, an amount above the outstanding balance and a missing method —
 * each with its own message. Checking here means the operator sees it while typing rather than after
 * pressing a button labelled *take payment*.
 */
export function settleProblems(form: SettleForm, tab: Tab): SettleProblem[] {
  const problems: SettleProblem[] = []
  const raw = form.amount.trim().replace(',', '.')
  const parsed = Number(raw)
  if (raw === '' || !Number.isFinite(parsed)) {
    problems.push('not-a-number')
  } else {
    const amount = Math.round(parsed * 100)
    if (amount <= 0) problems.push('not-positive')
    else if (amount > tab.outstandingCents) problems.push('over-outstanding')
  }
  if (form.paymentMethod === '') problems.push('no-method')
  return problems
}

export function settleProblemMessage(problem: SettleProblem, tab: Tab): string {
  if (problem === 'not-a-number') return tt('Suma musí byť číslo.')
  if (problem === 'not-positive') return tt('Suma úhrady musí byť kladná.')
  if (problem === 'over-outstanding') {
    return `${tt('Úhrada prevyšuje zostatok účtu')} ${formatCents(tab.outstandingCents)}.`
  }
  return tt('Vyberte spôsob úhrady.')
}

export function canSettle(form: SettleForm, tab: Tab): boolean {
  return isOpen(tab) && settleProblems(form, tab).length === 0
}

export function settlePayload(form: SettleForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    amount: Number(form.amount.trim().replace(',', '.')),
    paymentMethod: form.paymentMethod,
  }
  if (form.note.trim() !== '') payload.note = form.note.trim()
  return payload
}

/**
 * Whether this payment closes the tab.
 *
 * The server settles *"the moment nothing is left, not on a separate action"* — so a part-payment that
 * happens to clear the balance closes it, and the operator should know that before pressing.
 */
export function wouldClose(form: SettleForm, tab: Tab): boolean {
  const parsed = Number(form.amount.trim().replace(',', '.'))
  if (!Number.isFinite(parsed)) return false
  return Math.round(parsed * 100) === tab.outstandingCents && tab.outstandingCents > 0
}

export function settleSummary(form: SettleForm, tab: Tab): string {
  if (settleProblems(form, tab).length > 0) return ''
  const amount = Math.round(Number(form.amount.trim().replace(',', '.')) * 100)
  const left = tab.outstandingCents - amount
  const fiscal = tt('Doklad sa zaregistruje na túto sumu a na tento spôsob úhrady - účet sám sa nefiskalizuje.')
  if (left === 0) {
    return `${tt('Táto úhrada účet uzavrie.')} ${fiscal}`
  }
  return `${tt('Na účte ostane')} ${formatCents(left)}. ${fiscal}`
}

// ─── Putting an order on a tab ───────────────────────────────────────────────

/**
 * Every order id already on some tab.
 *
 * **`tab_id` is not in the orders projection**, so an order list cannot say whether it is already on a
 * tab — and `append` would silently move it, because it is a plain `UPDATE orders SET tab_id`. The tabs
 * themselves carry their orders, so the answer is computed from `/order-tabs/all` rather than guessed.
 * Filed as a backend item all the same: a screen that has not loaded every tab cannot do this.
 */
export function ordersOnAnyTab(tabs: readonly Tab[]): ReadonlySet<number> {
  const held = new Set<number>()
  for (const tab of tabs) {
    for (const order of tab.orders) held.add(order.id)
  }
  return held
}

export interface CandidateOrder {
  id: number
  orderNumber: string
  type: string
  totalCents: number
  customerName: string | null
}

export function candidatesOf(payload: unknown): CandidateOrder[] {
  const rows = (payload as { data?: unknown } | undefined)?.data
  if (!Array.isArray(rows)) return []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        orderNumber: text(row.orderNumber) ?? String(row.id),
        type: text(row.type) ?? '',
        totalCents: cents(row.total),
        customerName: text(row.customerName),
      },
    ]
  })
}

/**
 * The orders that may go on this tab.
 *
 * Three conditions, and each one is the server's: the tab is open, the order's channel allows deferred
 * payment, and the order is not already on a tab. The third is the one an order list cannot answer for
 * itself — see `ordersOnAnyTab`.
 */
export function eligibleForTab(
  candidates: readonly CandidateOrder[],
  tab: Tab,
  allowsTabFor: (slug: string) => boolean,
  onSomeTab: ReadonlySet<number>,
): CandidateOrder[] {
  if (!isOpen(tab)) return []
  return candidates.filter(
    (order) => allowsTabFor(order.type) && !onSomeTab.has(order.id),
  )
}

/** Why there is nothing to add, which is never the same as *there are no orders*. */
export function noCandidatesNote(
  candidates: readonly CandidateOrder[],
  allowsTabFor: (slug: string) => boolean,
  onSomeTab: ReadonlySet<number>,
): string {
  if (candidates.length === 0) {
    return tt('Za zvolené obdobie nie sú žiadne objednávky.')
  }
  const wrongChannel = candidates.filter((order) => !allowsTabFor(order.type)).length
  const alreadyOn = candidates.filter((order) => onSomeTab.has(order.id)).length
  if (wrongChannel === candidates.length) {
    return tt(
      'Ani jedna z nich nie je na kanáli s odloženou platbou, takže na účet nemôže ísť - inak by vypadla z očakávanej tržby zmeny.',
    )
  }
  if (alreadyOn > 0) {
    return `${formatCount(alreadyOn)} ${tt('z nich už na nejakom účte je.')}`
  }
  return tt('Nič, čo by sa dalo pridať.')
}
